import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useClerk } from '@clerk/vue';
import { watch } from 'vue';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/Home.vue'),
    meta: { requiresAuth: false, layout: 'public' },
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { requiresAuth: false, layout: 'auth' },
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/views/Dashboard.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/campaigns',
    name: 'Campaigns',
    component: () => import('@/views/Campaigns.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/campaigns/create',
    name: 'CampaignCreate',
    component: () => import('@/views/CampaignCreate.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/campaigns/:id',
    name: 'CampaignDetail',
    component: () => import('@/views/CampaignDetail.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/campaigns/:id/edit',
    name: 'CampaignEdit',
    component: () => import('@/views/CampaignEdit.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/traffic-sources',
    name: 'TrafficSources',
    component: () => import('@/views/TrafficSourcesView.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/traffic-sources/new',
    name: 'TrafficSourceCreate',
    component: () => import('@/views/TrafficSourceFormView.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/traffic-sources/:id/edit',
    name: 'TrafficSourceEdit',
    component: () => import('@/views/TrafficSourceFormView.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/traffic-sources/:id',
    name: 'TrafficSourceDetail',
    component: () => import('@/views/TrafficSourceDetailView.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/reports',
    name: 'Reports',
    component: () => import('@/views/Reports.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/conversions',
    name: 'Conversions',
    component: () => import('@/views/ConversionsView.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/postback-logs',
    name: 'PostbackLogs',
    component: () => import('@/views/PostbackLogsView.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/landing-pages',
    name: 'LandingPages',
    component: () => import('@/views/LandingPagesView.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/offers',
    name: 'Offers',
    component: () => import('@/views/OffersView.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/alerts',
    name: 'Alerts',
    component: () => import('@/views/AlertsView.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/clicks',
    name: 'Clicks',
    component: () => import('@/views/ClicksView.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/api-tokens',
    name: 'ApiTokens',
    component: () => import('@/views/ApiTokensView.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/billing',
    name: 'Billing',
    component: () => import('@/views/BillingView.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/admin/orgs',
    name: 'AdminOrgs',
    component: () => import('@/views/AdminOrgsView.vue'),
    meta: { requiresAuth: true, layout: 'admin' },
  },
  {
    path: '/admin/billing',
    name: 'AdminBilling',
    component: () => import('@/views/AdminBillingView.vue'),
    meta: { requiresAuth: true, layout: 'admin' },
  },
  {
    path: '/testing',
    name: 'Testing',
    component: () => import('@/views/TestingView.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/contact',
    name: 'Contact',
    component: () => import('@/views/ContactView.vue'),
    meta: { requiresAuth: false, layout: 'public' },
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import('@/views/TermsView.vue'),
    meta: { requiresAuth: false, layout: 'public' },
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: () => import('@/views/PrivacyView.vue'),
    meta: { requiresAuth: false, layout: 'public' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: { requiresAuth: false, layout: 'public' },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, _from, next) => {
  const requiresAuth = to.meta.requiresAuth;

  if (!requiresAuth) {
    next();
    return;
  }

  const clerk = useClerk();

  // 等待 Clerk 初始化完成，不能直接放行
  if (!clerk.value?.loaded) {
    await new Promise<void>((resolve) => {
      const unwatch = watch(
        () => clerk.value?.loaded,
        (loaded) => { if (loaded) { unwatch(); resolve(); } },
        { immediate: true }
      );
    });
  }

  if (!clerk.value?.user) {
    next({ name: 'Login', query: { redirect: to.fullPath } });
  } else {
    next();
  }
});

export default router;
